import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import FormSelectField from '../../../common/components/formFields/FormSelectField';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import CommonButton from '../../../common/components/CommonButton';
import SettingsSectionHeader from '../../SettingsSectionHeader';
import {
  getVehicleMakeList,
  getVehicleModelList,
} from '../../../addvehicle/AddVehicleActions';
import { dataToSelect } from '../../../common/helpers/dataToSelect';
import { mapRangeToSelect } from '../utils/yearsRangeOptions';
import {
  createSearchAlert,
  getSearchAlertOptions,
  updateSearchAlert,
  validateSearchAlert,
} from '../SearchAlertActions';
import validationSchema from '../forms/CreateSearchAlertForm.schema';
import { ALL_SELECTED } from '../utils/defaultValue';
import CommonSweetAlert from '../../../common/components/CommonSweetAlert';
import { CommonSweetAlertVariants } from '../../../constants/CommonSweetAlertVariants';
import { useIsMounted } from '../utils/isMounted';
import '../../../assets/css/componentStyles/AddSearchAlertModal.css';

const MODAL_MODES = {
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
};

const DEFAULT_INITIAL_VALUES = {
  make: ALL_SELECTED,
  model: ALL_SELECTED,
  allRangeOfYears: true,
  yearMinValue: { value: -1, label: 'Any' },
  yearMaxValue: { value: -1, label: 'Any' },
  mileageMaxValue: { value: -1, label: 'All' },
  distanceMaxValueInMiles: { value: -1, label: 'National' },
};

const COMMON_FIELD_PROPS = {
  className: 'basic-data__input',
  labelVariant: 'bordered',
};

const addAllEmptyOption = (values, emptyLabel = 'All') => {
  const all = { value: -1, label: emptyLabel };
  return [all, ...values];
};

const valuesToSelect = (values) => {
  if (!values) return [];
  const formatMiles = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return values.map((item) => ({
    value: item.toString(),
    label: `Up to ${formatMiles(item)} miles`,
  }));
};

const mapInitialValues = (raw) => {
  if (!raw) return DEFAULT_INITIAL_VALUES;

  const isAll = (v) => v === undefined || v === null || v === -1;

  return {
    ...DEFAULT_INITIAL_VALUES,
    ...raw,
    make: raw.vehicleMakeId
      ? { value: raw.vehicleMakeId, label: raw.vehicleMakeName }
      : ALL_SELECTED,
    model: raw.vehicleModelId
      ? { value: raw.vehicleModelId, label: raw.vehicleModelName }
      : ALL_SELECTED,
    // Year fields: if not filled or -1, show 'Any'
    yearMinValue: !isAll(raw.yearTo)
      ? { value: raw.yearTo, label: String(raw.yearTo) }
      : { value: -1, label: 'Any' },
    yearMaxValue: !isAll(raw.yearFrom)
      ? { value: raw.yearFrom, label: String(raw.yearFrom) }
      : { value: -1, label: 'Any' },
    // Mileage: if not filled or -1, show 'All'
    mileageMaxValue: !isAll(raw.mileageMaxValue)
      ? {
          value: raw.mileageMaxValue,
          label: `Up to ${raw.mileageMaxValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} miles`,
        }
      : { value: -1, label: 'All' },
  };
};

function DeleteConfirmationModal({
  isOpen,
  toggle,
  onConfirm,
  initialValues,
  makeOptions,
  modelOptions,
  yearOptions,
  mileageOptions,
  items,
}) {
  const formInitialValues = mapInitialValues(initialValues);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      className="add-search-alert-modal"
      backdropClassName="add-address-modal-backdrop"
    >
      <ModalHeader toggle={toggle} tag="div">
        <SettingsSectionHeader header="Delete Search Alert" variant="noBorder" />
      </ModalHeader>
      <Formik initialValues={formInitialValues} onSubmit={() => {}} enableReinitialize>
        {() => (
          <Form>
            <ModalBody>
              <p className="mb-3 p-3">
                {items?.length === 1
                  ? "Are you sure you want to delete this alert? We'll stop notifying you when new vehicles are added to the system."
                  : 'Please confirm that you want to delete the following search alert:'}
              </p>

              <div className="p-3 rounded mb-3">
                <Row>
                  <Col md="6" sm="12">
                    <FormSelectField
                      name="make"
                      label="Make"
                      options={makeOptions}
                      value={formInitialValues.make}
                      disabled
                      {...COMMON_FIELD_PROPS}
                    />
                  </Col>
                  <Col md="6" sm="12">
                    <FormSelectField
                      name="model"
                      label="Model"
                      options={modelOptions}
                      value={formInitialValues.model}
                      disabled
                      {...COMMON_FIELD_PROPS}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="3">
                    <FormSelectField
                      name="yearMaxValue"
                      label="Year - FROM"
                      options={yearOptions}
                      value={formInitialValues.yearMaxValue}
                      disabled
                      {...COMMON_FIELD_PROPS}
                    />
                  </Col>
                  <Col md="3">
                    <FormSelectField
                      name="yearMinValue"
                      label="Year - TO"
                      options={yearOptions}
                      value={formInitialValues.yearMinValue}
                      disabled
                      {...COMMON_FIELD_PROPS}
                    />
                  </Col>
                  <Col md={6}>
                    <FormSelectField
                      name="mileageMaxValue"
                      label="Mileage"
                      options={mileageOptions}
                      value={formInitialValues.mileageMaxValue}
                      disabled
                      {...COMMON_FIELD_PROPS}
                    />
                  </Col>
                </Row>
              </div>

              <Row className="mt-4 mb-3">
                <Col xs={6}>
                  <CommonButton
                    label="DELETE"
                    variant={CommonButtonVariants.PRIMARY_RED}
                    handleClick={onConfirm}
                    className="w-100"
                    type="button"
                  />
                </Col>
                <Col xs={6}>
                  <CommonButton
                    label="Cancel"
                    variant={CommonButtonVariants.OUTLINED_RED}
                    handleClick={toggle}
                    className="w-100"
                    type="button"
                  />
                </Col>
              </Row>
            </ModalBody>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

function AddSearchAlertModal({
  isOpen,
  toggle,
  onSave,
  onEdit,
  initialValues,
  isEdit = false,
  isDelete = false,
}) {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const valueInMiles = useSelector((state) => state.searchAlerts.get('valueInMiles'));
  const yearsAgo = useSelector((state) => state.searchAlerts.get('yearsAgo'));
  const [dropdownState, setDropdownState] = useState({
    makeOptions: [],
    modelOptions: [],
    modelSelectDisabled: true,
  });

  const [alertState, setAlertState] = useState({
    title: '',
    message: '',
    formValues: null,
    formPayload: null,
  });

  const currentMode = useMemo(() => {
    if (isDelete) return MODAL_MODES.DELETE;
    if (isEdit) return MODAL_MODES.EDIT;
    return MODAL_MODES.ADD;
  }, [isDelete, isEdit]);

  const { yearOptions, mileageOptions } = useMemo(
    () => ({
      yearOptions: addAllEmptyOption(mapRangeToSelect(yearsAgo), 'Any'),
      mileageOptions: addAllEmptyOption(valuesToSelect(valueInMiles)),
    }),
    [yearsAgo, valueInMiles],
  );

  const formInitialValues = useMemo(() => {
    if (isEdit || isDelete) return mapInitialValues(initialValues);
    return initialValues ? mapInitialValues(initialValues) : DEFAULT_INITIAL_VALUES;
  }, [isEdit, isDelete, initialValues]);

  const resetStates = useCallback(() => {
    setAlertState({
      title: '',
      message: '',
      formValues: null,
      formPayload: null,
    });
    setDropdownState({
      makeOptions: [],
      modelOptions: [],
      modelSelectDisabled: true,
    });
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      resetStates();
    }
    toggle();
  }, [isOpen, toggle, resetStates]);

  const handleMakeSelectChange = useCallback(
    (e, setFieldValue) => {
      setFieldValue('model', { value: -1, label: 'ALL' });

      if (!e.value || e.value === -1) {
        setDropdownState((prev) => ({
          ...prev,
          modelOptions: [],
          modelSelectDisabled: true,
        }));
        return;
      }

      setDropdownState((prev) => ({ ...prev, modelSelectDisabled: false }));

      dispatch(
        getVehicleModelList(e.value, (modelList) => {
          setDropdownState((prev) => ({
            ...prev,
            modelOptions: dataToSelect(modelList?.data, ['id', 'name']),
          }));
        }),
      );
    },
    [dispatch],
  );

  const saveAlert = useCallback(
    async (values, form) => {
      form.setSubmitting(true);
      try {
        await dispatch(createSearchAlert(values, form.setErrors));
        if (isMounted.current) {
          resetStates();
          onSave();
          toggle();
        }
      } finally {
        if (isMounted.current) {
          form.setSubmitting(false);
        }
      }
    },
    [dispatch, isMounted, onSave, toggle, resetStates],
  );

  const handleSubmit = useCallback(
    async (values, form) => {
      form.setSubmitting(true);

      try {
        if (currentMode === MODAL_MODES.EDIT && initialValues?.id) {
          const res = await dispatch(
            validateSearchAlert(initialValues.id, values, form.setErrors),
          );
          if (res.code === 'NO_ACTION') {
            await dispatch(updateSearchAlert(initialValues.id, values, form.setErrors));
            if (isMounted.current) {
              resetStates();
              if (onEdit) onEdit();
            }
          } else {
            setAlertState({
              title: res.title,
              message: res.message,
              formValues: values,
              formPayload: form,
            });
          }
        } else {
          const res = await dispatch(validateSearchAlert(null, values, form.setErrors));
          if (res.code === 'NO_ACTION') {
            await saveAlert(values, form);
          } else {
            setAlertState({
              title: res.title,
              message: res.message,
              formValues: values,
              formPayload: form,
            });
          }
        }
      } finally {
        if (isMounted.current) {
          form.setSubmitting(false);
        }
      }
    },
    [currentMode, initialValues, dispatch, isMounted, resetStates, onEdit, saveAlert],
  );

  const handleAlertConfirm = useCallback(() => {
    if (alertState.formValues && alertState.formPayload) {
      saveAlert(alertState.formValues, alertState.formPayload);
    }
  }, [saveAlert, alertState.formValues, alertState.formPayload]);

  const handleAlertCancel = useCallback(() => {
    setAlertState({
      title: '',
      message: '',
      formValues: null,
      formPayload: null,
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      dispatch(
        getVehicleMakeList((makeList) => {
          setDropdownState((prev) => ({
            ...prev,
            makeOptions: addAllEmptyOption(dataToSelect(makeList?.data, ['id', 'name'])),
          }));
        }),
      );
      dispatch(getSearchAlertOptions());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (isOpen && initialValues?.vehicleMakeId) {
      dispatch(
        getVehicleModelList(initialValues.vehicleMakeId, (modelList) => {
          setDropdownState((prev) => ({
            ...prev,
            modelOptions: dataToSelect(modelList?.data, ['id', 'name']),
            modelSelectDisabled: false,
          }));
        }),
      );
    }
  }, [dispatch, isOpen, initialValues]);

  const modalTitle = useMemo(() => {
    switch (currentMode) {
      case MODAL_MODES.DELETE:
        return 'Delete Search Alert';
      case MODAL_MODES.EDIT:
        return 'Edit Search Alert';
      default:
        return 'Add Search Alert';
    }
  }, [currentMode]);

  const items = useSelector((state) => state.searchAlerts.get('searchAlerts'));

  if (currentMode === MODAL_MODES.DELETE) {
    return (
      <DeleteConfirmationModal
        isOpen={isOpen}
        toggle={handleToggle}
        onConfirm={onSave}
        initialValues={initialValues}
        makeOptions={dropdownState.makeOptions}
        modelOptions={dropdownState.modelOptions}
        yearOptions={yearOptions}
        mileageOptions={mileageOptions}
        items={items}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      centered
      size="lg"
      className="add-search-alert-modal"
      backdropClassName="add-address-modal-backdrop"
    >
      <ModalHeader toggle={handleToggle} tag="div">
        <SettingsSectionHeader header={modalTitle} variant="noBorder" />
      </ModalHeader>
      <Formik
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <ModalBody>
              <Row>
                <Col md="6" sm="12">
                  <FormSelectField
                    name="make"
                    label="Make"
                    options={dropdownState.makeOptions}
                    onChangeHandler={(e) => handleMakeSelectChange(e, setFieldValue)}
                    {...COMMON_FIELD_PROPS}
                  />
                </Col>
                <Col md="6" sm="12">
                  <FormSelectField
                    name="model"
                    label="Model"
                    disabled={dropdownState.modelSelectDisabled}
                    options={dropdownState.modelOptions}
                    {...COMMON_FIELD_PROPS}
                  />
                </Col>
              </Row>
              <Row className="d-flex align-items-center  mt-3">
                <Col md="3">
                  <FormSelectField
                    name="yearMaxValue"
                    label="Year - FROM"
                    options={yearOptions}
                    {...COMMON_FIELD_PROPS}
                  />
                </Col>
                <Col md="3">
                  <FormSelectField
                    name="yearMinValue"
                    label="Year - TO"
                    options={yearOptions}
                    {...COMMON_FIELD_PROPS}
                  />
                </Col>
                <Col md={6}>
                  <FormSelectField
                    name="mileageMaxValue"
                    label="Mileage"
                    options={mileageOptions}
                    {...COMMON_FIELD_PROPS}
                  />
                </Col>
              </Row>
              <Row className="mt-4 mb-3">
                <Col xs={6}>
                  <CommonButton
                    label="Save"
                    variant={CommonButtonVariants.PRIMARY_RED}
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    className="w-100"
                  />
                </Col>
                <Col xs={6}>
                  <CommonButton
                    label="Cancel"
                    variant={CommonButtonVariants.OUTLINED_RED}
                    handleClick={handleToggle}
                    className="w-100"
                    type="button"
                  />
                </Col>
              </Row>
            </ModalBody>
          </Form>
        )}
      </Formik>
      <CommonSweetAlert
        variant={CommonSweetAlertVariants.ERROR}
        show={alertState.title !== ''}
        onConfirm={handleAlertConfirm}
        onCancel={handleAlertCancel}
      >
        <p className="common-sweet-alert__title">{alertState.message}</p>
      </CommonSweetAlert>
    </Modal>
  );
}

AddSearchAlertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

AddSearchAlertModal.defaultProps = {
  onEdit: undefined,
  initialValues: undefined,
  isEdit: false,
};

export default AddSearchAlertModal;
